import React, { useState, useEffect } from 'react';
import ServerCard from './ServerCard';
import './ServerList.css';

const ServerList = () => {
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortBy, setSortBy] = useState('upvotePower');
  const [selectedTag, setSelectedTag] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchServers();
    // Parse URL for initial tag filter
    const urlPath = window.location.pathname;
    const tagMatch = urlPath.match(/^\/tag\/(.+)$/);
    if (tagMatch) {
      const tagFromUrl = decodeURIComponent(tagMatch[1]);
      setSelectedTag(tagFromUrl);
    }

    // Set up automatic refresh every 10 seconds (silent refresh)
    const refreshInterval = setInterval(() => {
      fetchServers(false);
    }, 10000);

    return () => clearInterval(refreshInterval);
  }, []);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const urlPath = window.location.pathname;
      const tagMatch = urlPath.match(/^\/tag\/(.+)$/);
      if (tagMatch) {
        const tagFromUrl = decodeURIComponent(tagMatch[1]);
        setSelectedTag(tagFromUrl);
      } else {
        setSelectedTag(null);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const fetchServers = async (showLoading = true) => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      const response = await fetch('https://api.vmp.ir/server/list/list.json');
      if (!response.ok) {
        throw new Error('Failed to fetch servers');
      }
      const data = await response.json();
      setServers(data);
      setError(null); // Clear any previous errors on successful fetch
    } catch (err) {
      setError(err.message);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const sortServers = (servers, sortType) => {
    return [...servers].sort((a, b) => {
      switch (sortType) {
        case 'upvotePower':
          return (b.upvotePower || 0) - (a.upvotePower || 0);
        case 'playerCount':
          return (b.clients || 0) - (a.clients || 0);
        case 'serverName':
          const nameA = a.vars?.sv_projectName || 'Unknown Server';
          const nameB = b.vars?.sv_projectName || 'Unknown Server';
          return nameA.localeCompare(nameB);
        default:
          return 0;
      }
    });
  };

  const filterServersByTag = (servers, tag) => {
    if (!tag) return servers;
    return servers.filter(server => {
      const serverTags = server.vars?.tags ? server.vars.tags.split(',').map(t => t.trim().toLowerCase()).filter(t => t) : [];
      return serverTags.includes(tag.toLowerCase());
    });
  };

  const filterServersBySearch = (servers, searchTerm) => {
    if (!searchTerm) return servers;
    const lowercaseSearch = searchTerm.toLowerCase();
    return servers.filter(server => {
      const serverName = server.vars?.sv_projectName || '';
      const serverDescription = server.vars?.sv_projectDesc || '';
      const serverTags = server.vars?.tags || '';
      const serverOwner = server.ownerName || '';
      
      return (
        serverName.toLowerCase().includes(lowercaseSearch) ||
        serverDescription.toLowerCase().includes(lowercaseSearch) ||
        serverTags.toLowerCase().includes(lowercaseSearch) ||
        serverOwner.toLowerCase().includes(lowercaseSearch)
      );
    });
  };

  const tagFilteredServers = filterServersByTag(servers, selectedTag);
  const searchFilteredServers = filterServersBySearch(tagFilteredServers, searchTerm);
  const sortedServers = sortServers(searchFilteredServers, sortBy);

  const handleTagClick = (tag) => {
    setSelectedTag(tag);
    // Update URL without page reload
    window.history.pushState({}, '', `/tag/${encodeURIComponent(tag)}`);
  };

  const clearTagFilter = () => {
    setSelectedTag(null);
    window.history.pushState({}, '', '/');
  };

  if (loading) {
    return (
      <div className="server-list-container">
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading servers...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="server-list-container">
        <div className="error">
          <h3>Error loading servers</h3>
          <p>{error}</p>
          <p>Retrying automatically...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="server-list-container">
      <div className="server-list-header">
        <div className="header-title-section">
          <h2>
            {selectedTag ? (
              <>
                Servers with tag "{selectedTag}" ({sortedServers.length})
                <button onClick={clearTagFilter} className="clear-filter-button">
                  ✕ Clear Filter
                </button>
              </>
            ) : searchTerm ? (
              `Search results (${sortedServers.length})`
            ) : (
              `Available Servers (${servers.length})`
            )}
          </h2>
        </div>
        <div className="header-controls">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search servers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            {searchTerm && (
              <button 
                onClick={() => setSearchTerm('')}
                className="clear-search-button"
                title="Clear search"
              >
                ✕
              </button>
            )}
          </div>
          <div className="sort-dropdown">
            <label htmlFor="sort-select">Sort by:</label>
            <select 
              id="sort-select"
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value)}
              className="sort-select"
            >
              <option value="upvotePower">Most Boosted</option>
              <option value="playerCount">Player Count</option>
              <option value="serverName">Server Name</option>
            </select>
          </div>
        </div>
      </div>
      <div className="server-grid">
        {sortedServers.map((server) => (
          <ServerCard key={server.id} server={server} onTagClick={handleTagClick} />
        ))}
      </div>
    </div>
  );
};

export default ServerList;